import React, { useEffect, useState } from "react";
import { Button, Col, Form, Input, message, Row, Select, Space, Typography } from "antd";
import { ArrowLeftOutlined, DownloadOutlined, SaveOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { routes } from "common/routes";
import API, { apis } from "common/apis";
import {
  createPlatformConfig,
  IPlatformConfigDetail,
  selectPlatformConfigState,
} from "store/platform-config/platformConfigSlice";
import ConfigEntriesEditor from "./components/ConfigEntriesEditor";
import {
  configFromEntries,
  DEFAULT_VERSION,
  entriesFromConfig,
  IConfigEntry,
  PLATFORM_OPTIONS,
} from "./configUtils";

const { Text } = Typography;

// Version like 3.2.1 (2-4 numeric segments).
const VERSION_PATTERN = /^\d+(\.\d+){1,3}$/;

/**
 * Create a new config version for a platform. Entries can be started from
 * scratch or pre-filled from the platform's default config.
 */
const PlatformConfigCreatePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { saveStatus } = useAppSelector(selectPlatformConfigState);

  const [platform, setPlatform] = useState<string | undefined>(
    searchParams.get("platform") ?? undefined
  );
  const [version, setVersion] = useState("");
  const [entries, setEntries] = useState<IConfigEntry[]>([]);
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);

  useEffect(() => {
    // Reset the working set when the target platform changes.
    setEntries([]);
  }, [platform]);

  const loadDefaults = async () => {
    if (!platform) return;
    setIsLoadingDefaults(true);
    try {
      const response = await API.get<IPlatformConfigDetail>(
        `${apis.platformConfig}/${encodeURIComponent(platform)}/${DEFAULT_VERSION}`
      );
      setEntries(entriesFromConfig(response.data?.config ?? {}));
      message.success("Loaded entries from the default config");
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || "Failed to load the default config"
      );
    } finally {
      setIsLoadingDefaults(false);
    }
  };

  const handleCreate = async () => {
    if (!platform) {
      message.error("Please select a platform.");
      return;
    }
    const trimmedVersion = version.trim();
    if (!VERSION_PATTERN.test(trimmedVersion)) {
      message.error("Please enter a valid version, e.g. 3.2.1");
      return;
    }
    const { config, error } = configFromEntries(entries);
    if (error || !config) {
      message.error(error);
      return;
    }
    if (!Object.keys(config).length) {
      message.error("Add at least one config entry.");
      return;
    }
    try {
      await dispatch(
        createPlatformConfig({ name: platform, version: trimmedVersion, config })
      ).unwrap();
      message.success(`Config ${platform} v${trimmedVersion} created`);
      navigate(`/${routes.PLATFORM_CONFIGS.ALL}?platform=${platform}`);
    } catch (err: any) {
      message.error(err?.message || "Failed to create config");
    }
  };

  const isSaving = saveStatus === "saving";

  return (
    <>
      <Row justify="space-between" align="middle">
        <Col>
          <Space align="center">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() =>
                navigate(
                  `/${routes.PLATFORM_CONFIGS.ALL}${platform ? `?platform=${platform}` : ""}`
                )
              }
            />
            <AppPageHeader title="New Config Version" />
          </Space>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleCreate}
            loading={isSaving}
          >
            Create Config
          </Button>
        </Col>
      </Row>

      <Form layout="vertical" className="mb-8">
        <Row gutter={24} align="bottom">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="Platform" required className="mb-4">
              <Select
                showSearch
                placeholder="Select platform"
                className="w-full"
                value={platform}
                onChange={(value: string) => setPlatform(value)}
                options={PLATFORM_OPTIONS.map((name) => ({ value: name, label: name }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="Version" required className="mb-4">
              <Input
                placeholder="e.g. 3.2.1"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
              />
            </Form.Item>
          </Col>
          {platform && (
            <Col>
              <Form.Item className="mb-4">
                <Button
                  icon={<DownloadOutlined />}
                  onClick={loadDefaults}
                  loading={isLoadingDefaults}
                >
                  Pre-fill from Default Config
                </Button>
              </Form.Item>
            </Col>
          )}
        </Row>
        <Row>
          <Col>
            <Text type="secondary">
              Only the keys added here will override the platform&apos;s default config
              for this version.
            </Text>
          </Col>
        </Row>
      </Form>

      <Row>
        <Col xs={24}>
          <ConfigEntriesEditor
            entries={entries}
            onChange={setEntries}
            disabled={isSaving}
          />
        </Col>
      </Row>
    </>
  );
};

export default PlatformConfigCreatePage;
